import React, {useEffect, useState} from 'react';
import {
  ApiProviderProto,
  GetLoginsForBusinessRequest,
  GetLoginsForBusinessResponse,
  IApiProviderProto,
  MutateTeamMemberRequest,
  MutateTeamMemberResponse
} from "../../../../provider_api";
import {useRpcSender} from "../../../../util/RpcSender";
import {useParams} from "react-router-dom";
import {RpcState} from "../../../../util/RpcState";
import {EditableStringField} from "../../../../components/Editable/EditableStringField";
import {KensuObjectEditor} from "../../../../components/table/KensuObjectEditor";
import {EditableMultiSelect} from "../../../../components/Editable/EditableMultiSelect";
import {BusinessProfile} from "../BusinessProfile/BusinessProfile";
import {useProviderProfile} from "../../../../ProviderProfileProvider";
import {EditableSingleSelect} from "../../../../components/Editable/EditableSingleSelect";
import {shortDateTimeFormat2} from "../../../../util/DateTimeUtils";
import {isProviderEmailVerified, isProviderMobileNumberVerified} from "../../../../util/ProviderUtil";


/**
 * This updates a team member's login. Really the only thing that should
 * be editable here is roles and disabling, and only business admins
 * can do those.
 */
export const UpdateTeamMember = () => {

  // If editing, the provider id is a param. 
  const {loginIdParam} = useParams();

  const {businessProfile} = useProviderProfile();

  // Login info is loaded via a separate RPC. This lives outside the profile
  const [login, setLogin] = useState<IApiProviderProto | null>();
  const [loginRpcState, setLoginRpcState] = useState<RpcState>({});

  const rpcSender = useRpcSender();


  useEffect(() => {

    if (!loginIdParam) {
      return;
    }

    const request = GetLoginsForBusinessRequest.encode(new GetLoginsForBusinessRequest({
      providerIds: [loginIdParam]
    })).finish();

    setLoginRpcState({state: 'pending'});
    rpcSender.send('get_logins', request)
        .then(r => {
          const response = GetLoginsForBusinessResponse.decode(r);
          if (!response.okay) {
            setLoginRpcState({state: 'error', message: 'unknown error fetching login'});
            return;
          }

          if (response.provider.length > 0) {
            if (response.provider[0].providerId != loginIdParam) {
              console.log('Error: provider ids not matching!');
              setLoginRpcState({state: 'error', message: 'wrong provider id loaded!'});
              setLogin(null);
              return;
            }

            setLogin(response.provider[0])
            setLoginRpcState({state: 'success'});
            return;
          }

          // Success but there was no login found.
          setLogin(null);
          setLoginRpcState({state: 'success'});

        })
        .catch(e => {
          setLoginRpcState({state: 'error', message: e});
        })
  }, []);


  // This is only an editing situation. No creating new team members here,
  // that only happens via invite.
  if (!loginIdParam) {
    return <div>404</div>
  }


  // TODO make KensuObjectEditor work with something random
  return <KensuObjectEditor<IApiProviderProto, IApiProviderProto>
      idParam={loginIdParam}
      entityName={'Team Member'}
      entityListViewLink={'../logins'}
      getter={id => login}
      getTitle={bio => bio.lastLoginEmail}
      newBlankType={() => new ApiProviderProto()}
      parseRpcResponse={MutateTeamMemberResponse.decode}
      getRpcMethod={_ => 'update_team_member'}
      handleContainerTypeUpdate={setLogin}
      getRpcPayload={teamMember => MutateTeamMemberRequest.encode(
          new MutateTeamMemberRequest({
            teamMember: teamMember
          })).finish()}>


    <EditableStringField<IApiProviderProto>
        label={'Name'}
        errorKey={'name'}
        getter={p => p.profile?.firstName + ' ' + p.profile?.lastName}
        inputType={'text'}
        immutable
    />

    <EditableStringField<IApiProviderProto>
        label={'Last login email'}
        field={'lastLoginEmail'}
        inputType={'text'}
        immutable
    />

    <EditableStringField<IApiProviderProto>
        label={'Contact email'}
        getter={entity => {
          const email = entity.contactEmail;
          if (email) {
            return `${email} ${isProviderEmailVerified(entity, email) ? '(verified)' : '(unverified)'}`;
          } else {
            return '';
          }
        }}
        inputType={'email'}
        immutable
    />

    <EditableStringField<IApiProviderProto>
        label={'Mobile number'}
        getter={entity => {
          const mobile = entity.mobileNumber;
          if (mobile) {
            return `${mobile} ${isProviderMobileNumberVerified(entity, mobile) ? '(verified)' : '(unverified)'}`;
          } else {
            return '';
          }
        }}
        inputType={'email'}
        immutable
    />

    <EditableSingleSelect<IApiProviderProto, string>
        label={'Linked practitioner'}
        field={'linkedPractitionerId'}
        getter={p => p.linkedPractitionerId}
        setter={(entity, value) => ({...entity, linkedPractitionerId: value})}
        options={[{
          label: '[None]',
          value: ''
        },
          ...(businessProfile?.practitioners?.map(p => ({
            label: p.firstName + ' ' + p.lastName,
            value: p.practitionerId as string
          })) ?? [])
        ]}
    />

    <EditableStringField<IApiProviderProto>
        label={'Last login time'}
        getter={p => shortDateTimeFormat2(p.lastLoginTimestamp)}
        inputType={'text'}
        immutable
    />

    <EditableMultiSelect<IApiProviderProto, string>
        label={'Roles'}
        field={'businessRoles'}
        getter={entity => entity.businessRoles ?? []}
        setter={(entity, value) => ({...entity, businessRoles: value})}
        options={[
          {label: 'Read self', value: 'self.read'},
          {label: 'Edit self', value: 'self.edit'},
          {label: 'Business Admin', value: 'business_admin'}
        ]}/>

    <EditableStringField<IApiProviderProto>
        label={'Login ID'}
        field={'loginId'}
        inputType={'text'}
        immutable
    />

  </KensuObjectEditor>;
}